<script>
import { ref, computed } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAI } from 'dashboard/composables/useAI';
import AICTAModal from './AICTAModal.vue';
import AIAssistanceModal from './AIAssistanceModal.vue';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import AIAssistanceCTAButton from './AIAssistanceCTAButton.vue';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

export default {
  components: {
    NextButton,
    AIAssistanceModal,
    AICTAModal,
    AIAssistanceCTAButton,
  },
  emits: ['replaceText'],
  setup(props, { emit }) {
    const { uiSettings, updateUISettings } = useUISettings();

    const {
      isAIIntegrationEnabled,
      draftMessage,
      recordAnalytics,
      providerTranslationKey,
      aiProviders,
      aiProviderId,
      setAIProvider,
    } = useAI();
    const { t } = useI18n();

    const { isAdmin } = useAdmin();

    const initialMessage = ref('');
    const showProviderMenu = ref(false);

    const initializeMessage = draftMsg => {
      initialMessage.value = draftMsg;
    };
    const keyboardEvents = {
      '$mod+KeyZ': {
        action: () => {
          if (initialMessage.value) {
            emit('replaceText', initialMessage.value);
            initialMessage.value = '';
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    const aiAssistLabel = computed(() =>
      t(`INTEGRATION_SETTINGS.${providerTranslationKey.value}.AI_ASSIST`)
    );

    const hasMultipleProviders = computed(() => aiProviders.value.length > 1);

    const toggleProviderMenu = () => {
      showProviderMenu.value = !showProviderMenu.value;
    };

    const selectProvider = providerId => {
      setAIProvider(providerId);
      showProviderMenu.value = false;
    };

    return {
      uiSettings,
      updateUISettings,
      isAdmin,
      initialMessage,
      initializeMessage,
      recordAnalytics,
      isAIIntegrationEnabled,
      draftMessage,
      aiAssistLabel,
      aiProviders,
      aiProviderId,
      hasMultipleProviders,
      showProviderMenu,
      toggleProviderMenu,
      selectProvider,
    };
  },
  data: () => ({
    showAIAssistanceModal: false,
    showAICtaModal: false,
    aiOption: '',
  }),
  computed: {
    isAICTAModalDismissed() {
      return this.uiSettings.is_open_ai_cta_modal_dismissed;
    },
    // Display a AI CTA button for admins if the AI integration has not been added yet and the AI assistance modal has not been dismissed.
    shouldShowAIAssistCTAButtonForAdmin() {
      return (
        this.isAdmin &&
        !this.isAIIntegrationEnabled &&
        !this.isAICTAModalDismissed
      );
    },
    // Display a AI CTA button for agents and other admins who have not yet opened the AI assistance modal.
    shouldShowAIAssistCTAButton() {
      return this.isAIIntegrationEnabled && !this.isAICTAModalDismissed;
    },
  },

  mounted() {
    emitter.on(CMD_AI_ASSIST, this.onAIAssist);
    this.initializeMessage(this.draftMessage);
  },

  methods: {
    hideAIAssistanceModal() {
      this.recordAnalytics('DISMISS_AI_SUGGESTION', {
        aiOption: this.aiOption,
      });
      this.showAIAssistanceModal = false;
    },
    openAIAssist() {
      // Dismiss the CTA modal if it is not dismissed
      if (!this.isAICTAModalDismissed) {
        this.updateUISettings({
          is_open_ai_cta_modal_dismissed: true,
        });
      }
      this.initializeMessage(this.draftMessage);
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'ai_assist' });
    },
    hideAICtaModal() {
      this.showAICtaModal = false;
    },
    openAICta() {
      this.showAICtaModal = true;
    },
    onAIAssist(option) {
      this.aiOption = option;
      this.showAIAssistanceModal = true;
    },
    insertText(message) {
      this.$emit('replaceText', message);
    },
  },
};
</script>

<template>
  <div class="relative flex items-center gap-1">
    <!-- Always show AI button -->
    <template v-if="isAIIntegrationEnabled">
      <!-- Show CTA button for first-time users -->
      <AIAssistanceCTAButton
        v-if="!isAICTAModalDismissed"
        :label="aiAssistLabel"
        @open="openAIAssist"
      />
      <!-- Show regular button for returning users -->
      <NextButton
        v-else
        v-tooltip.top-end="aiAssistLabel"
        icon="i-ph-magic-wand"
        slate
        faded
        sm
        @click="openAIAssist"
      />
    </template>
    <!-- Show setup button for admins without AI integration -->
    <template v-else-if="isAdmin">
      <AIAssistanceCTAButton
        v-if="!isAICTAModalDismissed"
        :label="aiAssistLabel"
        @open="openAICta"
      />
      <NextButton
        v-else
        v-tooltip.top-end="'Setup AI Assistant'"
        icon="i-ph-magic-wand"
        slate
        faded
        sm
        @click="openAICta"
      />
    </template>

    <!-- Provider selector dropdown -->
    <div v-if="isAIIntegrationEnabled && hasMultipleProviders" class="relative">
      <NextButton
        v-tooltip.top-end="'Select AI Provider'"
        icon="i-ph-caret-down"
        slate
        faded
        sm
        @click="toggleProviderMenu"
      />
      <div
        v-if="showProviderMenu"
        v-on-clickaway="() => (showProviderMenu = false)"
        class="absolute right-0 bottom-full mb-2 w-48 rounded-lg bg-n-slate-1 dark:bg-n-slate-2 shadow-lg border border-n-slate-6 dark:border-n-slate-7 z-[9999]"
      >
        <div class="p-1">
          <button
            v-for="provider in aiProviders"
            :key="provider.id"
            class="w-full text-left px-3 py-2 rounded text-sm hover:bg-n-slate-3 dark:hover:bg-n-slate-4 flex items-center justify-between"
            :class="{
              'bg-n-slate-3 dark:bg-n-slate-4 font-medium':
                provider.id === aiProviderId,
            }"
            @click="selectProvider(provider.id)"
          >
            <span>{{ provider.name }}</span>
            <span
              v-if="provider.id === aiProviderId"
              class="text-n-brand i-ph-check w-4 h-4"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <woot-modal
      v-if="isAIIntegrationEnabled"
      v-model:show="showAIAssistanceModal"
      :on-close="hideAIAssistanceModal"
    >
      <AIAssistanceModal
        :ai-option="aiOption"
        @apply-text="insertText"
        @close="hideAIAssistanceModal"
      />
    </woot-modal>
    <woot-modal v-model:show="showAICtaModal" :on-close="hideAICtaModal">
      <AICTAModal @close="hideAICtaModal" />
    </woot-modal>
  </div>
</template>
